import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "../Reveal";
import SectionHeading from "../SectionHeading";
import Icon from "../Icon";
import { SOLUTIONS, SOLUTION_DETAIL_SLUGS, IMAGES } from "../../data/content";

function ServiceBlock({ s, index }) {
  const reversed = index % 2 === 1;
  const hasDetail = SOLUTION_DETAIL_SLUGS.includes(s.slug);
  const to = hasDetail ? `/solutions/${s.slug}` : "/contact";
  return (
    <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16" data-testid={`service-block-${s.slug}`}>
      {/* Visual */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? 40 : -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`group relative overflow-hidden rounded-[1.5rem] border border-[#111827]/8 shadow-[0_30px_60px_-40px_rgba(17,24,39,0.4)] ${reversed ? "lg:order-2" : ""}`}
      >
        <div className="img-overlay-blue relative">
          <img
            src={IMAGES[s.image]}
            alt={`${s.title} visual`}
            loading="lazy"
            className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-[440px]"
          />
        </div>
        <div className="absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 text-[#1677FF] backdrop-blur">
          <Icon name={s.icon} className="h-6 w-6" />
        </div>
        <span className="absolute bottom-5 right-6 text-6xl font-extrabold text-white/80">{s.number}</span>
      </motion.div>

      {/* Content */}
      <div className={reversed ? "lg:order-1" : ""}>
        <Reveal>
          <span className="ad-eyebrow">Service {s.number}</span>
          <h3 className="mt-3 text-2xl font-extrabold text-[#111827] sm:text-3xl lg:text-4xl">{s.title}</h3>
          <p className="mt-4 text-base leading-relaxed text-[#4B5563]">{s.intro}</p>
        </Reveal>
        <Reveal index={1}>
          <div className="mt-6 grid gap-2 sm:grid-cols-2">
            {s.useCases.slice(0, 4).map((u) => (
              <div key={u} className="flex items-center gap-2 text-sm text-[#374151]">
                <Check className="h-4 w-4 flex-shrink-0 text-[#1677FF]" />
                {u}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal index={2}>
          <Link
            to={to}
            className="group mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] transition-colors hover:text-[#00C7D9]"
          >
            {hasDetail ? s.cta : "Talk to us"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}

export default function ServicesEditorial({ heading = true }) {
  return (
    <section className="ad-section bg-white" id="solutions" data-testid="services-editorial">
      <div className="ad-container">
        {heading && (
          <SectionHeading
            eyebrow="Solutions"
            title={<>Technology built around <span className="text-gradient">your business.</span></>}
            sub="Six connected capabilities, presented as stories — from raw data to intelligent decisions."
            className="mb-16 lg:mb-24"
          />
        )}
        <div className="space-y-20 lg:space-y-32">
          {SOLUTIONS.map((s, i) => (
            <ServiceBlock key={s.slug} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
